import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { apiClient } from '@/api/client';
import {
  API_MAIL_MESSAGES,
  API_MAIL_MESSAGES_DELETE_PERMANENTLY,
  API_MAIL_MESSAGES_READ,
  API_MAIL_MESSAGES_READ_ALL,
} from '@/api/endpoints';
import { useRefreshProfiles } from '@/hooks/useRefreshProfiles';
import type { useMailMessages } from '@/pages/Mail/hooks/useMailMessages';
import type { useMailSelection } from '@/pages/Mail/hooks/useMailSelection';
import type { MailFolderType } from '@/types/components';
import { ErrorMessage, logRequestError } from '@/utils/errors';

const toMessageIds = (ids: Set<string>) => [...ids].map(Number);

interface Params {
  folderType: MailFolderType;
  messages: ReturnType<typeof useMailMessages>;
  selection: ReturnType<typeof useMailSelection>;
}

export const useMailActions = ({ folderType, messages, selection }: Params) => {
  const [isMarkAllReadSnackbarOpen, setIsMarkAllReadSnackbarOpen] =
    useState(false);
  const [isErrorSnackbarOpen, setIsErrorSnackbarOpen] = useState(false);

  const { selectedIds } = selection;

  const refreshProfiles = useRefreshProfiles();

  const failAction = (message: ErrorMessage, error: unknown) => {
    logRequestError(message, error);
    setIsErrorSnackbarOpen(true);
  };

  const refreshMessages = () => {
    messages.refresh();
    selection.exitSelection();
  };

  const refreshMessagesAndProfiles = async () => {
    refreshMessages();
    await refreshProfiles();
  };

  const markAllReadMutation = useMutation({
    mutationFn: () => apiClient.put(API_MAIL_MESSAGES_READ_ALL),
    onSuccess: async () => {
      messages.refresh();
      setIsMarkAllReadSnackbarOpen(true);
      await refreshProfiles();
    },
    onError: (error) => failAction(ErrorMessage.MarkAllMailMessagesRead, error),
  });

  const markReadMutation = useMutation({
    mutationFn: (ids: Set<string>) =>
      apiClient.patch(API_MAIL_MESSAGES_READ, {
        messageIds: toMessageIds(ids),
      }),
    onSuccess: refreshMessagesAndProfiles,
    onError: (error) => failAction(ErrorMessage.MarkMailMessagesRead, error),
  });

  const deleteMutation = useMutation({
    mutationFn: (ids: Set<string>) =>
      apiClient.patch(API_MAIL_MESSAGES, {
        isDeleted: true,
        messageIds: toMessageIds(ids),
      }),
    onSuccess: refreshMessagesAndProfiles,
    onError: (error) => failAction(ErrorMessage.DeleteMailMessages, error),
  });

  const restoreMutation = useMutation({
    mutationFn: (ids: Set<string>) =>
      apiClient.patch(API_MAIL_MESSAGES, {
        isDeleted: false,
        messageIds: toMessageIds(ids),
      }),
    onSuccess: refreshMessagesAndProfiles,
    onError: (error) => failAction(ErrorMessage.RestoreMailMessages, error),
  });

  const deletePermanentlyMutation = useMutation({
    mutationFn: (ids: Set<string>) =>
      apiClient.post(API_MAIL_MESSAGES_DELETE_PERMANENTLY, {
        messageIds: toMessageIds(ids),
        folderType,
      }),
    onSuccess: refreshMessages,
    onError: (error) =>
      failAction(ErrorMessage.PermanentlyDeleteMailMessages, error),
  });

  const isPending =
    markAllReadMutation.isPending ||
    markReadMutation.isPending ||
    deleteMutation.isPending ||
    restoreMutation.isPending ||
    deletePermanentlyMutation.isPending;

  return {
    isPending,
    isMarkAllReadSnackbarOpen,
    isErrorSnackbarOpen,
    markAllRead: () => markAllReadMutation.mutate(),
    markSelectedRead: () => markReadMutation.mutate(selectedIds),
    deleteSelected: () => deleteMutation.mutate(selectedIds),
    restoreSelected: () => restoreMutation.mutate(selectedIds),
    deleteSelectedPermanently: () =>
      deletePermanentlyMutation.mutate(selectedIds),
    deleteMail: (id: string, onDeleted: () => void) =>
      deleteMutation.mutate(new Set([id]), { onSuccess: onDeleted }),
    deleteMailPermanently: (id: string, onDeleted: () => void) =>
      deletePermanentlyMutation.mutate(new Set([id]), { onSuccess: onDeleted }),
    dismissMarkAllReadSnackbar: () => setIsMarkAllReadSnackbarOpen(false),
    dismissErrorSnackbar: () => setIsErrorSnackbarOpen(false),
  };
};
